import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import qs from 'querystringify';
import React, { Component } from 'react';

import jsonGet from '../../../util/jsonGet';
import jsonPost from '../../../util/jsonPost';
import { inspireMeDropdownValue } from './constants';

import Button from '../Atoms/Button';
import Dropdown from '../Atoms/Dropdown';
import ErrorBoundary from '../Core/ErrorBoundary';
import Loading from '../Core/Loading';
import FeatureCarousel from '../FeatureCarousel';
import Filter from './Filter';
import InspireMe from './InspireMe';
import Results from './Results';
import TournamentFilter from './TournamentFilter';
import PackageFilterContainer from './modules/PackageFilterContainer';
import { getInitialCurrencyState } from '../../../util/currency';
import { addCultureAndFallbackToParamsFromLocalStorage } from '../../../util/culture';
import getDefaultFilterOptionsForSelectedCurrency from './helpers';
import URL_PARAMS from '../../../util/urlParams';
import CUSTOM_EVENT from '../../../util/customEvents';

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  autoplay: PropTypes.bool.isRequired,
  crmApiURL: PropTypes.string.isRequired,
  defaultSortOrder: PropTypes.number.isRequired,
  featuredItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  filters: PropTypes.object.isRequired,
  fromPriceText: PropTypes.string,
  hideFilters: PropTypes.bool.isRequired,
  hideSearchResults: PropTypes.bool.isRequired,
  interval: PropTypes.number.isRequired,
  labels: PropTypes.object.isRequired,
  limitedAvailabilityText: PropTypes.string.isRequired,
  loadMoreButtonText: PropTypes.string,
  perPersonText: PropTypes.string,
  noResultsMessage: PropTypes.string.isRequired,
  resetButtonText: PropTypes.string,
  resultsPerPage: PropTypes.number.isRequired,
  searchResultsLayout: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  soldOutText: PropTypes.string.isRequired,
  viewTourText: PropTypes.string,
  waitingListText: PropTypes.string,
});

const defaultProps = {
  fromPriceText: 'from',
  loadMoreButtonText: 'Load More',
  resetButtonText: 'reset',
  viewTourText: 'view package',
  waitingListText: null,
  perPersonText: 'pp',
};

const searchResultsLayout = {
  List: 0,
  Grid: 1,
};

const tourSearchSortOrder = {
  none: 0,
  priceDescending: 1,
  priceAscending: 2,
  durationDescending: 3,
  durationAscending: 4,
};

const setScrollHistory = () => {
  try {
    const scrollTop = document.scrollingElement
      ? document.scrollingElement.scrollTop
      : document.documentElement.scrollTop;
    if (scrollTop) {
      window.localStorage.setItem('scrollState', scrollTop);
    }
  } catch (error) {
    console.error('Error saving scroll state', error);
  }
};

class PackageSearch extends Component {
  constructor(props) {
    super(props);

    this.handlePaginationClick = this.handlePaginationClick.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleValueChange = this.handleValueChange.bind(this);
    this.handleTournamentValueChange = this.handleTournamentValueChange.bind(this);
    this.setQuery = this.setQuery.bind(this);

    this.tournamentFilterRef = React.createRef(); // we need to specifically instruct tournament filters to reset.

    this.sortOptions = [
      {
        text: props.labels.defaultSortOrderText
          ? `${props.labels.sortOrderLabelText}: ${props.labels.defaultSortOrderText}`
          : props.labels.sortOrderLabelText,
        value: tourSearchSortOrder.none,
      },
      {
        text: `${props.labels.sortOrderLabelText}: ${props.labels.priceAscendingSortOrderText}`,
        value: tourSearchSortOrder.priceAscending,
      },
      {
        text: `${props.labels.sortOrderLabelText}: ${props.labels.priceDescendingSortOrderText}`,
        value: tourSearchSortOrder.priceDescending,
      },
      {
        text: `${props.labels.sortOrderLabelText}: ${props.labels.durationAscendingSortOrderText}`,
        value: tourSearchSortOrder.durationAscending,
      },
      {
        text: `${props.labels.sortOrderLabelText}: ${props.labels.durationDescendingSortOrderText}`,
        value: tourSearchSortOrder.durationDescending,
      },
    ];
    const initialCurrency = getInitialCurrencyState(props.filters.prices);

    this.state = {
      filterOptions: getDefaultFilterOptionsForSelectedCurrency({
        currencyCode: initialCurrency.code,
        filters: props.filters,
        resultsPerPage: this.props.resultsPerPage,
        siteId: this.props.siteId,
        defaultSortOrder: this.props.defaultSortOrder,
      }),
      currentCurrency: getInitialCurrencyState(props.filters.prices),
      items: null,
      loadingPage: false,
      loadingResults: true,
      tournamentID: null,
      packageTypes: '',
      tourTypeSubFilters: [],
      totalItemCount: 0,
      initialScroll: 0,
      scrolled: false,
      lastPage: 1,
    };
  }

  componentDidMount() {
    this.handleQuery((filterOptions) => {
      this.setQuery(filterOptions);
    });
    sessionStorage.removeItem('selectedFlights');
    window.addEventListener('scroll', setScrollHistory, { passive: true });
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
    window.addEventListener('popstate', this.handlePopState);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', setScrollHistory);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
    window.removeEventListener('popstate', this.handlePopState);
  }

  setQuery(filterOptions, pushHistory = true) {
    const paramString = qs.stringify(
      {
        ...(filterOptions || {}),
        [URL_PARAMS.CURRENCY]: this.state.currentCurrency.code,
      },
      true
    );
    const path = window.location.pathname + paramString;
    if (pushHistory) {
      window.history.pushState(null, null, path);
    } else {
      window.history.replaceState(null, null, path);
    }
  }

  handleCurrencyChange = (event) => {
    const newCurrencyCode = event?.detail?.code; // user selected new currency
    // Determine which filter price in what currency to use dependent on the newly selected currency
    const newCurrencyFilterDataWithMinAndMaxValues = this.props.filters.prices.find(
      (price) => price.currency === newCurrencyCode
    );

    if (newCurrencyFilterDataWithMinAndMaxValues) {
      this.setState(
        (prevState) => ({
          currentCurrency: event.detail,
          filterOptions: {
            ...prevState.filterOptions,
            priceFrom: newCurrencyFilterDataWithMinAndMaxValues.minimumPriceValue,
            priceTo: newCurrencyFilterDataWithMinAndMaxValues.maximumPriceValue,
            minPrice: newCurrencyFilterDataWithMinAndMaxValues.minimumPriceValue,
            maxPrice: newCurrencyFilterDataWithMinAndMaxValues.maximumPriceValue,
          },
        }),
        () => {
          this.setQuery(this.state.filterOptions);
        }
      );
    } else {
      console.error('Currency data not found for:', newCurrencyCode);
    }
  };

  handleQuery(callback) {
    if (window.location.search) {
      const currentCurrencyCode = this.state.currentCurrency.code;
      const currentCurrencyData = this.props.filters.prices.find(
        (price) => price.currency === currentCurrencyCode
      );

      const parsedQuery = qs.parse(window.location.search);

      const minPrice =
        Number(parsedQuery[URL_PARAMS.MIN_PRICE]) || currentCurrencyData.minimumPriceValue;
      const maxPrice =
        Number(parsedQuery[URL_PARAMS.MAX_PRICE]) || currentCurrencyData.maximumPriceValue;
      const parsedPriceFrom = Number(parsedQuery[URL_PARAMS.PRICE_FROM]) || minPrice;
      const parsedPriceTo = Number(parsedQuery[URL_PARAMS.PRICE_TO]) || maxPrice;

      // Ensure priceFrom and priceTo are within the valid range
      const priceFrom = Math.max(parsedPriceFrom, minPrice);
      const priceTo = Math.min(parsedPriceTo, maxPrice);

      this.setState(
        (prevState) => {
          const filterOptions = {
            ...prevState.filterOptions,
            ...parsedQuery,
            [URL_PARAMS.PAGE]: prevState.filterOptions.page || 1,
            [URL_PARAMS.RESULTS_PER_PAGE]:
              Number(parsedQuery[URL_PARAMS.RESULTS_PER_PAGE]) ||
              prevState.filterOptions.resultsPerPage,
            [URL_PARAMS.SITE_ID]:
              Number(parsedQuery[URL_PARAMS.SITE_ID]) || prevState.filterOptions.siteId,
            [URL_PARAMS.TOURNAMENTS]:
              (parsedQuery[URL_PARAMS.TOURNAMENTS] &&
                parsedQuery[URL_PARAMS.TOURNAMENTS].split(',')) ||
              prevState.filterOptions.tournaments,
            [URL_PARAMS.DURATIONS]:
              (parsedQuery[URL_PARAMS.DURATIONS] && parsedQuery[URL_PARAMS.DURATIONS].split(',')) ||
              [],
            [URL_PARAMS.ACCOMMODATION_TYPE_IDS]:
              (parsedQuery[URL_PARAMS.ACCOMMODATION_TYPE_IDS] &&
                parsedQuery[URL_PARAMS.ACCOMMODATION_TYPE_IDS].split(',')) ||
              [],
            [URL_PARAMS.MATCHES]:
              (parsedQuery[URL_PARAMS.MATCHES] && parsedQuery[URL_PARAMS.MATCHES].split(',')) || [],
            [URL_PARAMS.DESTINATIONS]:
              (parsedQuery[URL_PARAMS.DESTINATIONS] &&
                parsedQuery[URL_PARAMS.DESTINATIONS].split(',')) ||
              [],
            [URL_PARAMS.PACKAGE_TYPES]: parsedQuery[URL_PARAMS.PACKAGE_TYPES] || '',
            [URL_PARAMS.SORT_ORDER]:
              Number(parsedQuery[URL_PARAMS.SORT_ORDER]) || prevState.filterOptions.sortOrder,
            priceFrom,
            priceTo,
            minPrice,
            maxPrice,
          };

          const initialScroll = Number(localStorage.scrollState) || 0;
          const lastPage = Number(parsedQuery[URL_PARAMS.PAGE]) || null;

          return {
            filterOptions,
            initialScroll,
            lastPage,
          };
        },
        () => {
          this.loadResults();
          if (callback) callback();
        }
      );
    } else {
      // No URL parameters; reset filters to default
      this.setState(
        (prevState) => ({
          filterOptions: getDefaultFilterOptionsForSelectedCurrency({
            currencyCode: prevState.currentCurrency.code,
            filters: this.props.filters,
            resultsPerPage: this.props.resultsPerPage,
            siteId: this.props.siteId,
            defaultSortOrder: this.props.defaultSortOrder,
          }),
          loadingResults: true,
        }),
        () => {
          this.loadResults();
          if (callback) callback(this.state.filterOptions);
        }
      );
    }
  }

  handlePopState = () => {
    this.handleQuery(() => {
      this.loadResults(false, false);
    });
  };

  handlePaginationClick = () => {
    this.setState(
      (prevState) => ({
        filterOptions: {
          ...prevState.filterOptions,
          page: prevState.filterOptions.page + 1,
        },
        loadingPage: true,
      }),
      () => {
        this.loadResults(true);
      }
    );
  };

  handleValueChange = (payload) => {
    this.setState(
      (prevState) => ({
        filterOptions: {
          ...prevState.filterOptions,
          ...payload,
          page: 1, // Reset page when filters change
        },
        loadingResults: true,
      }),
      () => {
        this.loadResults();
        this.setQuery(this.state.filterOptions);
      }
    );
  };

  handleTournamentValueChange = (payload) => {
    const updatedPayload = { ...payload };
    if (updatedPayload.tournaments.length === 0) {
      // if we have no tournaments selected then we want to tell the api to pull in all.
      updatedPayload.tournaments = this.props.filters.tournaments.map((t) => t.id);
    }
    updatedPayload.matches = [];
    this.handleValueChange(updatedPayload);
  };

  handlePackageValueChange = (packageTypes, tourTypeSubFilters) => {
    this.handleValueChange({ packageTypes, tourTypeSubFilters });
  };

  handleReset() {
    if (this.tournamentFilterRef.current) {
      this.tournamentFilterRef.current.reset();
    }
    this.setState(
      (prevState) => ({
        filterOptions: getDefaultFilterOptionsForSelectedCurrency({
          currencyCode: prevState.currentCurrency.code,
          filters: this.props.filters,
          resultsPerPage: this.props.resultsPerPage,
          siteId: this.props.siteId,
          defaultSortOrder: this.props.defaultSortOrder,
        }),
        loadingResults: true,
      }),
      () => {
        this.loadResults();
        this.setQuery(this.state.filterOptions);
      }
    );
  }

  // eslint-disable-next-line default-param-last
  loadResults = (concatItemSets = false, pushHistory = true) => {
    const filterOptions = { ...this.state.filterOptions };
    let tournamentID = null;

    // Store timestamp as API request ID
    const apiRequestID = Date.now();
    this.apiRequestID = apiRequestID;

    const { matches } = filterOptions;
    const { tournaments, inspireMe } = this.props.filters;

    const updatedFilterOptions = { ...filterOptions };
    // include all matches as query if all matches are selected and a tournament is selected
    if (filterOptions.tournaments && (!matches || (matches && !matches.length))) {
      const allMatches = tournaments
        .filter((tournament) => filterOptions.tournaments.includes(tournament.id))
        .reduce((prev, cur) => [...prev, ...cur.matches.map(({ id }) => id)], []);
      updatedFilterOptions.matches = allMatches;
    }

    if (inspireMe && inspireMe.length > 0) {
      const allDestinations = inspireMe.destinations.items.map((x) => x.id);
      updatedFilterOptions.destinations = allDestinations;
      filterOptions.destinations = allDestinations;
    }

    if (updatedFilterOptions.packageTypes === inspireMeDropdownValue) {
      updatedFilterOptions.packageTypes = '';
    }

    if (updatedFilterOptions.packageTypes === '') {
      delete updatedFilterOptions.packageTypes;
    }

    if (
      updatedFilterOptions.toDate === '' ||
      updatedFilterOptions.toDate === updatedFilterOptions.fromDate
    ) {
      delete updatedFilterOptions.toDate;
    }

    if (
      updatedFilterOptions.tourTypeSubFilters === null ||
      updatedFilterOptions.tourTypeSubFilters?.length === 0
    ) {
      delete updatedFilterOptions.tourTypeSubFilters;
    }

    const tourSearchApiRequestWithCulture =
      addCultureAndFallbackToParamsFromLocalStorage(updatedFilterOptions);
    const tourSearchApiRequestOptionsWithCultureAndCurrency = {
      ...tourSearchApiRequestWithCulture,
      currency: this.state.currentCurrency.code,
    };

    // Get JSON results
    jsonGet(this.props.apiURL, tourSearchApiRequestOptionsWithCultureAndCurrency, (data) => {
      let { items } = data;

      // Check no new API requests have been made
      if (apiRequestID !== this.apiRequestID) return;

      // Optionally concat results array with existing results
      if (concatItemSets) {
        const currentItems = this.state.items || [];
        items = currentItems.concat(items);
      }

      // Update the tournamentID when a single tournament is selected.
      if (filterOptions.tournaments && filterOptions.tournaments.length === 1) {
        [tournamentID] = filterOptions.tournaments;
      }

      const { lastPage } = this.state;
      let nextPage = Number(filterOptions.page);
      const loadNextPage = Number(lastPage) && Number(lastPage) > nextPage;

      if (loadNextPage) {
        nextPage += 1;
        filterOptions.page = nextPage;
      }

      // Update state results and flags
      this.setState(
        {
          filterOptions,
          packageTypes: filterOptions.packageTypes,
          tourTypeSubFilters: filterOptions.tourTypeSubFilters,
          items,
          loadingPage: false,
          loadingResults: false,
          tournamentID,
          totalItemCount: data.totalItemCount,
        },
        () => {
          this.setQuery(filterOptions, pushHistory);
          if (!this.state.scrolled) {
            this.setState({ scrolled: true });
            const parsedQuery = qs.parse(window.location.search) || {};
            if (this.state.initialScroll && parsedQuery.page) {
              window.scrollTo(0, Number(this.state.initialScroll));
            }
          }

          // Load multiple pages if a page is passed from query
          if (loadNextPage) {
            this.loadResults(true);
          } else if (Number(lastPage) === Number(filterOptions.page)) {
            this.setState({
              lastPage: null,
            });
          }
          // Dispatch custom event to notify CompareListComponent of re-render
          window.dispatchEvent(new CustomEvent(CUSTOM_EVENT.GET_COMPARE_BUTTON_STATE_ON_REMOUNT));
        }
      );
    });

    const { filters } = this.props;

    const crmApiRequest = {
      siteId: updatedFilterOptions.siteId.toString(),
      fromDate: updatedFilterOptions.fromDate,
      toDate: updatedFilterOptions.toDate,
      priceFrom: updatedFilterOptions.priceFrom,
      priceTo: updatedFilterOptions.priceTo,
      currency: this.state.currentCurrency.code,
      tournaments: filters.tournaments
        ?.filter((x) => updatedFilterOptions.tournaments?.includes(x.id))
        .map((y) => y.title),
      matches: [
        ...new Set(
          filters.tournaments
            ?.flatMap((f) => f.matches)
            .filter((x) => updatedFilterOptions.matches?.includes(x.id))
            .map((y) => y.title)
        ),
      ],
      durations: filters.durations
        ?.filter((x) => updatedFilterOptions.durations?.includes(x.id))
        .map((y) => y.title),
      packageTypes: filters.packageTypes
        ?.filter((x) => updatedFilterOptions.packageTypes?.includes(x.id))
        .map((y) => y.title),
      accommodationTypeIds: filters.accommodationTypes
        ?.filter((x) => updatedFilterOptions.accommodationTypeIds?.includes(x.id))
        .map((y) => y.title),
      destinations: filters.inspireMe?.destinations?.items
        ?.filter((x) => updatedFilterOptions.destinations?.includes(x.id))
        .map((y) => y.title),
      tourTypeSubFilters: [
        ...new Set(
          filters?.packageTypes
            ?.flatMap((f) => f?.tourTypeSubFilter || [])
            .filter((x) => x && updatedFilterOptions?.tourTypeSubFilters?.includes(x.id))
            .map((y) => y?.title)
        ),
      ],
      holidayTypes: filters.inspireMe?.holidayTypes?.items
        ?.filter((x) => updatedFilterOptions.holidayTypes?.includes(x.id))
        .map((y) => y.title),
      attractions: filters.inspireMe?.attractions?.items
        ?.filter((x) => updatedFilterOptions.attractions?.includes(x.id))
        .map((y) => y.title),
      partySizes: filters.inspireMe?.partySizes?.items
        ?.filter((x) => updatedFilterOptions.partySizes?.includes(x.id))
        .map((y) => y.title),
      eventType: '', // TODO: Unclear but required, pass in empty string for now for BE
    };

    jsonPost(this.props.crmApiURL, crmApiRequest, (response) => {
      if (response.errorCode) console.error(response.errorMessage || response.message);
    });
  };

  render() {
    const useGridLayout = this.props.searchResultsLayout === searchResultsLayout.Grid;

    const tournamentDestinationOptions = this.props.filters.tournaments.find(
      (tournament) => tournament.id === this.state.tournamentID
    )?.destinations;

    return (
      <section className="PackageSearch">
        <ErrorBoundary>
          <header className="PackageSearch_header">
            <div className="PackageSearch_featureCarousel">
              <FeatureCarousel
                autoplay={this.props.autoplay}
                fromPriceText={this.props.fromPriceText}
                items={this.props.featuredItems}
                interval={this.props.interval}
                limitedAvailabilityText={this.props.limitedAvailabilityText}
                perPersonText={this.props.perPersonText}
                sideButtons
                soldOutText={this.props.soldOutText}
                currentCurrency={this.state.currentCurrency}
                viewTourText={this.props.viewTourText}
              />
            </div>
          </header>
          {this.props.filters.tournaments &&
            this.props.filters.tournaments.length > 1 &&
            !this.props.hideFilters && (
              <PackageFilterContainer>
                <TournamentFilter
                  allOptionText={this.props.filters.allOptionText}
                  selectedTournaments={this.state.filterOptions.tournaments}
                  tournaments={this.props.filters.tournaments}
                  handleReset={this.handleReset}
                  handleValueChange={this.handleTournamentValueChange}
                  ref={this.tournamentFilterRef}
                />
              </PackageFilterContainer>
            )}
          {this.props.filters.packageTypes &&
            this.props.filters.packageTypes.length > 0 &&
            !this.props.hideFilters && (
              <InspireMe
                allOptionText={this.props.filters.allOptionText}
                labels={this.props.labels}
                packageTypeSubFilterAllOptionText={
                  this.props.filters.packageTypeSubFilterAllOptionText
                }
                packageTypes={this.state.packageTypes}
                packages={this.props.filters.packageTypes}
                inspireMe={this.props.filters.inspireMe}
                destinations={this.state.filterOptions.destinations}
                partySizes={this.state.filterOptions.partySizes}
                attractions={this.state.filterOptions.attractions}
                holidayTypes={this.state.filterOptions.holidayTypes}
                tourTypeSubFilters={this.state.tourTypeSubFilters}
                handlePackageValueChange={this.handlePackageValueChange}
                handleValueChange={this.handleValueChange}
                tournamentDestinationOptions={tournamentDestinationOptions}
              />
            )}
          {this.state.items && !this.props.hideFilters && (
            <PackageFilterContainer>
              <Filter
                currentCurrency={this.state.currentCurrency}
                filterOptions={this.state.filterOptions}
                filters={this.props.filters}
                labels={this.props.labels}
                handleReset={this.handleReset}
                handleValueChange={this.handleValueChange}
                resetButtonText={this.props.resetButtonText}
                tournamentID={this.state.tournamentID}
              />
            </PackageFilterContainer>
          )}
          {!this.props.hideFilters && (
            <PackageFilterContainer>
              <div className="PackageSearch_sortContainer">
                <Dropdown
                  blockName="PackageSearch_sort"
                  fieldName="sortOrder"
                  fieldValue={this.state.filterOptions.sortOrder}
                  handleChange={(e) => this.handleValueChange({ sortOrder: e.target.value })}
                  options={this.sortOptions}
                />
              </div>
            </PackageFilterContainer>
          )}
          {!this.props.hideSearchResults && (
            <div className="PackageSearch_results">
              <div
                className={
                  useGridLayout
                    ? 'PackageSearch_resultsContentGrid'
                    : 'PackageSearch_resultsContent'
                }
              >
                {this.state.loadingResults && <Loading />}
                {!this.state.loadingResults && this.state.items && (
                  <>
                    <Results
                      fromPriceText={this.props.fromPriceText}
                      items={this.state.items}
                      limitedAvailabilityText={this.props.limitedAvailabilityText}
                      noResultsMessage={this.props.noResultsMessage}
                      soldOutText={this.props.soldOutText}
                      waitingListText={this.props.waitingListText}
                      useGridLayout={useGridLayout}
                      currentCurrency={this.state.currentCurrency}
                      viewTourText={this.props.viewTourText}
                      perPersonText={this.props.perPersonText}
                    />
                    {this.state.items.length < this.state.totalItemCount && (
                      <div className="PackageSearch_pagination">
                        <Button
                          buttonType="button"
                          handleClick={this.handlePaginationClick}
                          isLoading={this.state.loadingPage}
                          modifier="ghost"
                          text={this.props.loadMoreButtonText}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </ErrorBoundary>
      </section>
    );
  }
}

PackageSearch.propTypes = propTypes;
PackageSearch.defaultProps = defaultProps;

export default PackageSearch;
